import { CanceledSummary } from "@/queries/report";
import React, { useState } from "react";
import {
  CanceledFlightsModal,
  DisruptionModalFilter,
} from "@/components/disruption/CanceledFlightsModal";
import { DisruptionMetricTile } from "@/components/disruption/DisruptionMetricTile";
import {
  DisruptionScope,
  isLiveDisruptionContext,
  ReportType,
} from "@/components/disruption/disruptionMetrics";

interface DisruptionSectionProps {
  summary: CanceledSummary | null;
  reportId: number;
  reportType?: ReportType | null;
  scopeLabel?: string;
  scope?: DisruptionScope;
}

const EMPTY_SUMMARY: CanceledSummary = {
  total: 0,
  arrivals: 0,
  departures: 0,
  impacted_arrival_passengers: 0,
  impacted_departure_passengers: 0,
  impacted_passengers_total: 0,
};

export const DisruptionSection: React.FC<DisruptionSectionProps> = ({
  summary,
  reportId,
  reportType,
  scopeLabel,
  scope,
}) => {
  const data = summary ?? EMPTY_SUMMARY;
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFilter, setModalFilter] = useState<DisruptionModalFilter | null>(
    null
  );
  const [modalTitle, setModalTitle] = useState<string | null>(null);

  const isLiveContext = isLiveDisruptionContext(reportType);
  const tileVariant = isLiveContext ? "disruption" : "standard";

  const isAllZero =
    data.total === 0 && data.impacted_passengers_total === 0;

  const openModal = (filter: DisruptionModalFilter, title: string) => {
    setModalFilter(filter);
    setModalTitle(title);
    setModalOpen(true);
  };

  return (
    <div className="mt-8 md:mx-4">
      <div className="text-center text-xl sm:text-2xl text-amber-200 font-medium mb-1">
        Flight Disruption
      </div>
      {scopeLabel && (
        <div className="text-center text-sm text-slate-400 mb-2">
          {scopeLabel}
        </div>
      )}
      {isAllZero && (
        <div className="text-center text-sm text-slate-400 mb-4">
          No canceled flights at this time. No passenger demand removed at this
          time.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 mb-4">
        <DisruptionMetricTile
          name="Total Canceled Flights"
          description="Flights canceled today"
          value={data.total}
          metricKey="total_canceled"
          reportId={reportId}
          variant={tileVariant}
          enablePulse={isLiveContext}
          onClick={() => openModal("all_canceled", "Canceled Flights")}
        />
        <DisruptionMetricTile
          name="Canceled Arrivals"
          description="Arrival flights canceled"
          value={data.arrivals}
          metricKey="canceled_arrivals"
          reportId={reportId}
          variant={tileVariant}
          enablePulse={isLiveContext}
          onClick={() => openModal("canceled_arrivals", "Canceled Arrivals")}
        />
        <DisruptionMetricTile
          name="Canceled Departures"
          description="Departure flights canceled"
          value={data.departures}
          metricKey="canceled_departures"
          reportId={reportId}
          variant={tileVariant}
          enablePulse={isLiveContext}
          onClick={() =>
            openModal("canceled_departures", "Canceled Departures")
          }
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
        <DisruptionMetricTile
          name="Total Passenger Demand Removed"
          description="Estimated demand removed by cancellations"
          value={data.impacted_passengers_total}
          metricKey="impacted_total"
          reportId={reportId}
          variant={tileVariant}
          enablePulse={isLiveContext}
          onClick={() =>
            openModal("impacted_total", "Passenger Demand Removed")
          }
        />
        <DisruptionMetricTile
          name="Arrival Passenger Demand Removed"
          description="Estimated arrival demand removed by cancellations"
          value={data.impacted_arrival_passengers}
          metricKey="impacted_arrivals"
          reportId={reportId}
          variant={tileVariant}
          enablePulse={isLiveContext}
          onClick={() =>
            openModal(
              "impacted_arrivals",
              "Arrival Passenger Demand Removed"
            )
          }
        />
        <DisruptionMetricTile
          name="Departure Passenger Demand Removed"
          description="Estimated departure demand removed by cancellations"
          value={data.impacted_departure_passengers}
          metricKey="impacted_departures"
          reportId={reportId}
          variant={tileVariant}
          enablePulse={isLiveContext}
          onClick={() =>
            openModal(
              "impacted_departures",
              "Departure Passenger Demand Removed"
            )
          }
        />
      </div>
      <CanceledFlightsModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        reportId={reportId}
        filter={modalFilter}
        title={modalTitle}
        scope={scope}
      />
    </div>
  );
};
